(ns iris.posts.screening
  (:require
   [re-frame.core :as re-frame]
   [iris.events :as events]
   [iris.posts.helpers :refer [missing-screening-scopes
                               post-screening-scopes
                               screening-statuses-by-ruid]]))

(def reset-request-tracking-event :iris.posts.events/reset-screening-status-request-tracking)
(def queue-scopes-event :iris.posts.events/queue-screening-status-scopes)
(def clear-queued-scopes-event :iris.posts.events/clear-queued-screening-status-scopes)
(def mark-in-flight-event :iris.posts.events/mark-screening-status-in-flight)
(def mark-requested-event :iris.posts.events/mark-screening-status-requested)
(def clear-in-flight-event :iris.posts.events/clear-screening-status-in-flight)
(def get-status-event :iris.posts.events/http-get-screening-status)
(def get-status-success-event :iris.posts.events/http-get-screening-status-success)
(def get-status-failure-event :iris.posts.events/http-get-screening-status-failure)
(def http-complete-event :iris.posts.events/http-complete)

;; Reset per-load screening request tracking before a new post load starts.
(re-frame/reg-event-db
 reset-request-tracking-event
 (fn [db]
   (-> db
       (assoc-in [:posts :screening-status-queued] #{})
       (assoc-in [:posts :screening-status-in-flight] #{})
       (assoc-in [:posts :screening-status-requested] #{}))))

;; Queue rendered screening scopes from intermediate Post2a.List pages.
(re-frame/reg-event-db
 queue-scopes-event
 (fn [db [_ scopes]]
   (update-in db [:posts :screening-status-queued]
              (fnil into #{})
              scopes)))

;; Clear deferred scopes after final-page flush.
(re-frame/reg-event-db
 clear-queued-scopes-event
 (fn [db]
   (assoc-in db [:posts :screening-status-queued] #{})))

;; Mark scopes before ScreeningStatus.Get so concurrent batches do not repeat them.
(re-frame/reg-event-db
 mark-in-flight-event
 (fn [db [_ scopes]]
   (update-in db [:posts :screening-status-in-flight]
              (fnil into #{})
              scopes)))

;; Remember scopes requested since the current post-load cycle began.
;; This is intentionally cleared only by reset-request-tracking-event.
(re-frame/reg-event-db
 mark-requested-event
 (fn [db [_ scopes]]
   (update-in db [:posts :screening-status-requested]
              (fnil into #{})
              scopes)))

;; Release in-flight scopes when ScreeningStatus.Get succeeds or fails.
(re-frame/reg-event-db
 clear-in-flight-event
 (fn [db [_ scopes]]
   (update-in db [:posts :screening-status-in-flight]
              (fnil #(apply disj %1 %2) #{})
              scopes)))

;; Request a concrete set of scopes now after filtering duplicates in flight.
(defn request-screening-status-scopes [db scopes options]
  (when-let [ruids (missing-screening-scopes db scopes)]
    (re-frame/dispatch [mark-in-flight-event ruids])
    (re-frame/dispatch [mark-requested-event ruids])
    (re-frame/dispatch [get-status-event
                        {:ruids ruids}
                        (assoc options :requested-ruids ruids)])
    true))

;; Request screening statuses immediately for the given posts.
(defn request-screening-statuses
  ([db posts]
   (request-screening-statuses db posts nil))
  ([db posts options]
   (request-screening-status-scopes db (post-screening-scopes posts) options)))

;; Defer intermediate-page scopes until the page boundary is reached.
(defn queue-screening-statuses [posts]
  (when-let [scopes (post-screening-scopes posts)]
    (re-frame/dispatch [queue-scopes-event scopes])))

;; Request all queued scopes, plus optional final-batch scopes, in one call.
(defn flush-screening-statuses
  ([db]
   (flush-screening-statuses db nil nil))
  ([db posts]
   (flush-screening-statuses db posts nil))
  ([db posts options]
   (let [queued-scopes (get-in db [:posts :screening-status-queued])
         post-scopes (post-screening-scopes posts)
         requested? (request-screening-status-scopes
                     db
                     (concat queued-scopes post-scopes)
                     options)]
     (re-frame/dispatch [clear-queued-scopes-event])
     requested?)))

(re-frame/reg-event-fx
 get-status-success-event
 [(events/api-msg-interceptor {:mute-keys #{:result}})]
 (fn [{:keys [db]} [_ options data]]
   (let [statuses (screening-statuses-by-ruid (get-in data [:result :data]))
         dispatches (cond-> []
                      (not (:prevent-http-complete options))
                      (conj [http-complete-event])

                      (:requested-ruids options)
                      (conj [clear-in-flight-event (:requested-ruids options)]))]
     {:db (-> db
              (update-in [:posts :screening-status] merge statuses))
      :fx (mapv (fn [event] [:dispatch event]) dispatches)})))

(re-frame/reg-event-fx
 get-status-failure-event
 [(events/api-msg-interceptor {})]
 (fn [{:keys [db]} [_ options]]
   (let [dispatches (cond-> []
                      (:requested-ruids options)
                      (conj [clear-in-flight-event (:requested-ruids options)]))]
     {:db db
      :fx (mapv (fn [event] [:dispatch event]) dispatches)})))

(re-frame/reg-event-fx
 get-status-event
 (fn [_ [_ scopes options]]
   (let [data (cond
                (string? scopes) {:ruid scopes}
                (map? scopes) scopes
                (sequential? scopes) {:ruids (vec scopes)}
                :else scopes)]
     (let [fx (events/post-rpc {:jsonrpc "2.0"
                                :id 0
                                :method "ScreeningStatus.Get"
                                :params {:options {:debug false}
                                         :data data}}
                               [get-status-success-event options]
                               [get-status-failure-event options])]
       (when (:prevent-http-complete options)
         (re-frame/dispatch [http-complete-event]))
       fx))))
